import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Gift } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { languageOptions, roleLabels } from "@/i18n/dictionaries";
import { StarRating } from "@/components/StarRating";
import { ProfileForm } from "@/components/ProfileForm";
import { AddressBook } from "@/components/AddressBook";
import { PushToggle } from "@/components/PushToggle";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { loginPath } from "@/lib/loginPath";
import { maskedMobile } from "@/lib/format";
import { usePageTitle } from "@/lib/usePageTitle";
import { useAccountDeletion } from "@/lib/useAccountDeletion";
import { WORKER_APP_URL } from "@/lib/appLinks";
import { localized } from "@/theme/tenantTheme";
import { LEGAL_CONTACT } from "@/constants/legal";
import { useRoleSwitchConfirm } from "@/components/RoleSwitchConfirm";
import { GoogleLinkButton } from "@/components/GoogleLinkButton";
import { waHref } from "@/lib/phone";

const Card = ({ children, className = "", ...rest }) => (
  <section className={`rounded-2xl border border-border p-5 sm:p-6 ${className}`} {...rest}>
    {children}
  </section>
);
const H2 = ({ children, icon }) => (
  <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
    {icon} {children}
  </h2>
);

/** One "My activity" row: label, count, and - when there is nothing yet - the next action. */
function ActivityRow({ testId, label, count, to, emptyLabel, emptyTo, children }) {
  return (
    <div className="rounded-xl border border-border p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium">
          {label} <span className="ml-1 text-muted-foreground" data-testid={`${testId}-count`}>({count})</span>
        </p>
        {count > 0 && to && (
          <Link to={to} className="flex items-center text-sm text-[hsl(var(--primary))]">
            {children}
            <ChevronRight size={14} />
          </Link>
        )}
      </div>
      {count === 0 && (
        <Link to={emptyTo} className="mt-1 inline-block text-sm text-[hsl(var(--primary))]" data-testid={`${testId}-empty`}>
          {emptyLabel} →
        </Link>
      )}
    </div>
  );
}

export default function Account() {
  const { t, lang, auth, refreshMe, tenant, tenants = [], changeLanguage, signOut } = useApp();
  usePageTitle(t("account.title")); // R5-11: per-page tab title
  const navigate = useNavigate();
  const [emailForm, setEmailForm] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [rewards, setRewards] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);
  const [orderCount, setOrderCount] = useState(0);
  const [withdrawing, setWithdrawing] = useState(null);
  const [editing, setEditing] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { requestSwitch, dialog: roleSwitchDialog } = useRoleSwitchConfirm();
  const { remove: deleteAccount, busy: deleting } = useAccountDeletion();

  const loadReviews = () => api.get("/reviews/mine").then((r) => setReviews(rows(r.data))).catch(() => setReviews([]));

  useEffect(() => {
    if (!auth) {
      navigate(loginPath("/account", tenant?.slug));
      return;
    }
    // Each of these is independent and catches its own failure: one missing panel costs that panel
    // and nothing else. (`/rewards/summary` never existed; the capability is `/rewards/balance`.)
    api.get("/rewards/balance").then((r) => setRewards(r.data)).catch(() => setRewards(null));
    api.get("/referrals/mine").then((r) => setReferrals(r.data)).catch(() => setReferrals(null));
    loadReviews();
    api.get("/community/posts/mine").then((r) => setPosts(rows(r.data))).catch(() => setPosts([]));
    api
      .get("/activity/mine", { params: { tenant_id: tenant?.id, group: "all", limit: 100 } })
      .then((r) => setOrderCount(rows(r.data).length))
      .catch(() => setOrderCount(0));
  }, [auth, navigate, tenant?.id, tenant?.slug]);

  if (!auth) return null;

  const identity = auth.identity;
  const hasEmail = !!identity.email;
  const hasGoogle = (identity.auth_providers || []).includes("google");
  const isStaffOrOwner = (auth.role_profiles || []).some(
    (rp) => rp.role_type === "operator" || rp.role_type === "business_owner",
  );
  const cityName = localized(lang, tenant, "name");
  // C5-WEB: `waHref` adds the 91 country code and returns null for a junk number (hides the link).
  const deskWa = waHref(tenant?.operator_whatsapp);
  // R11 MULTICITY: "My roles" is this city's seats (`role_profiles`) plus the person's staff seats
  // in OTHER cities (`seats`: every active seat anywhere). A citizen seat elsewhere is just
  // browsing that city, so it is not listed. Switching to an other-city seat lands on that city.
  const roleList = [...(auth.role_profiles || [])];
  const otherCitySeats = (auth.seats || []).filter(
    (s) => s.role_type !== "citizen" && !roleList.some((rp) => rp.id === s.id),
  );
  roleList.push(...otherCitySeats);
  const cityLabel = (rp) => {
    const c = tenants.find((x) => x.id === rp.tenant_id);
    return lang === "hi" ? c?.name_hi || rp.tenant_name : rp.tenant_name;
  };
  const referralCode = identity.referral_code || "";
  const referralText = t("account.referralMessage", {
    code: referralCode,
    url: `${window.location.origin}/${tenant?.slug || ""}`,
  });

  const saveEmail = async () => {
    const addr = emailForm.trim();
    if (!addr) return;
    setEmailBusy(true);
    try {
      await api.patch("/auth/profile", { email: addr });
      await refreshMe();
      setEmailForm("");
      toast.success(t("account.emailSaved"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setEmailBusy(false);
    }
  };

  const withdrawReview = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      toast.success(t("account.reviewWithdrawn"));
      setWithdrawing(null);
      await loadReviews();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  // "Download my data" (DPDP): a copy of what we hold about the person, assembled from the
  // endpoints that already serve each part - no new backend surface.
  const downloadData = async () => {
    const get = (path, params) => api.get(path, { params }).then((r) => r.data).catch(() => null);
    const [addresses, myReviews, myPosts, balance, refs] = await Promise.all([
      get("/addresses"),
      get("/reviews/mine"),
      get("/community/posts/mine"),
      get("/rewards/balance"),
      get("/referrals/mine"),
    ]);
    const payload = {
      exported_at: new Date().toISOString(),
      profile: {
        display_name: identity.display_name,
        phone: identity.phone_e164,
        email: identity.email,
        gender: identity.gender,
        interests: identity.interests,
        referral_code: identity.referral_code,
      },
      addresses,
      reviews: myReviews,
      posts: myPosts,
      rewards: balance,
      referrals: refs,
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "apna-shahar-my-data.json";
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("account.dataDownloaded"));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-8 sm:px-8 sm:py-10" data-testid="account-page">
      {roleSwitchDialog}
      <h1 className="font-heading text-2xl font-bold tracking-tight">{t("account.title")}</h1>

      {/* 1. Header card */}
      <Card data-testid="account-header-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading text-xl font-semibold" data-testid="account-name">
              {identity.display_name || "—"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="account-phone">
              {maskedMobile(identity.phone_e164)}
            </p>
            <p className="text-sm text-muted-foreground" data-testid="account-city">
              {cityName}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full border border-border p-0.5" data-testid="account-language-toggle" aria-label={t("account.language")}>
              {languageOptions.map((o) => (
                <button
                  key={o.code}
                  type="button"
                  data-testid={`account-language-${o.code}`}
                  onClick={() => changeLanguage(o.code)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    lang === o.code ? "bg-[hsl(var(--primary))] text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="account-edit-profile" onClick={() => setEditing((v) => !v)}>
              {editing ? t("account.closeEdit") : t("account.editProfile")}
            </Button>
          </div>
        </div>
        {!identity.profile_completed && (
          <p
            className="mt-4 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 px-4 py-3 text-sm"
            data-testid="profile-incomplete-note"
          >
            {t("profile.incompleteNote")}
          </p>
        )}
        {editing && (
          <div className="mt-5 border-t border-border pt-5" data-testid="account-edit-panel">
            <ProfileForm variant="account" />
          </div>
        )}
      </Card>

      {/* 2. My activity */}
      <Card data-testid="account-activity-section">
        <H2>{t("account.activityTitle")}</H2>
        <div className="mt-4 space-y-3">
          <ActivityRow
            testId="activity-orders"
            label={t("account.ordersBookings")}
            count={orderCount}
            to="/activity"
            emptyLabel={t("account.emptyOrders")}
            emptyTo="/directory"
          >
            {t("account.seeAll")}
          </ActivityRow>
          <ActivityRow
            testId="activity-reviews"
            label={t("account.myReviews")}
            count={reviews.length}
            emptyLabel={t("account.emptyReviews")}
            emptyTo="/directory"
          />
          {(
            <div className="space-y-3" data-testid="my-reviews">
              {reviews.map((r) => (
                <article key={r.id} className="rounded-xl border border-border p-4" data-testid={`my-review-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium">{r.reviewable_name}</p>
                    <div className="flex items-center gap-3">
                      <StarRating value={r.rating} />
                      <Badge variant="outline">{t(`common.${r.status}`)}</Badge>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
                  {(r.status === "pending" || r.status === "approved") && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-xs text-muted-foreground" data-testid={`my-review-pending-note-${r.id}`}>
                        {r.status === "pending" ? t("account.reviewPendingNote") : t("account.reviewPublishedNote")}
                      </span>
                      {withdrawing === r.id ? (
                        <>
                          <Button size="sm" className="rounded-full" data-testid={`my-review-withdraw-confirm-${r.id}`} onClick={() => withdrawReview(r.id)}>
                            {t("account.withdrawReview")}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setWithdrawing(null)}>
                            {t("common.cancel")}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full" data-testid={`my-review-withdraw-${r.id}`} onClick={() => setWithdrawing(r.id)}>
                          {t("account.withdrawReview")}
                        </Button>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
          <ActivityRow
            testId="activity-posts"
            label={t("account.myPosts")}
            count={posts.length}
            emptyLabel={t("account.emptyPosts")}
            emptyTo="/community"
          />
          {(
            <div className="space-y-3" data-testid="my-posts">
              {posts.map((p) => (
                <article key={p.id} className="rounded-xl border border-border p-4" data-testid={`my-post-${p.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium">{p.title || p.body.slice(0, 60)}</p>
                    <Badge variant="outline">{t(`common.${p.status}`)}</Badge>
                  </div>
                  {p.status === "pending" && <p className="mt-1 text-xs text-muted-foreground">{t("account.postPendingNote")}</p>}
                </article>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* 3. Addresses (the one address book; per-address "print on bills") */}
      <AddressBook />

      {/* 4. Notifications */}
      <Card data-testid="account-notifications-section">
        <H2>{t("account.notificationsTitle")}</H2>
        <p className="mt-1 text-sm text-muted-foreground">{t("account.notificationsWhy")}</p>
        <div className="mt-4">
          <PushToggle />
        </div>
      </Card>

      {/* 5. Rewards: compact row, how to earn, share the referral code */}
      <Card data-testid="account-rewards-section">
        <div className="flex items-center justify-between gap-3">
          <H2 icon={<Gift size={17} className="text-[hsl(var(--accent))]" />}>{t("account.pointsShort")}</H2>
          <p className="font-heading text-2xl font-bold" data-testid="rewards-balance">
            {rewards?.balance ?? 0}
            <span className="ml-1.5 text-sm font-normal text-muted-foreground">{t("common.points")}</span>
          </p>
        </div>
        <details className="mt-2 text-sm" data-testid="rewards-how-to-earn">
          <summary className="cursor-pointer text-[hsl(var(--primary))]">{t("account.howToEarn")}</summary>
          <p className="mt-2 text-muted-foreground">{t("account.howToEarnBody")}</p>
        </details>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div>
            <p className="text-xs text-muted-foreground">{t("account.referral")}</p>
            <p className="text-lg font-semibold tracking-wide" data-testid="referral-code">
              {referralCode || "—"}
            </p>
          </div>
          {referralCode && (
            <Button asChild size="sm" variant="outline" className="rounded-full">
              <a
                href={waHref(null, referralText)}
                target="_blank"
                rel="noreferrer"
                data-testid="referral-share-whatsapp"
              >
                {t("account.referralShare")}
              </a>
            </Button>
          )}
        </div>
        {referrals?.items?.length > 0 && (
          <div className="mt-4 space-y-2 border-t border-border pt-4" data-testid="referrals-list">
            <p className="text-xs text-muted-foreground">{t("account.referrals")}</p>
            {referrals.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span>{item.referred_name}</span>
                <Badge variant={item.status === "rewarded" ? "default" : "outline"}>{t(`account.referralStatus.${item.status}`)}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 6. Roles - only when there is something to switch between */}
      {roleList.length > 1 && (
        <Card>
          <H2>{otherCitySeats.length ? t("account.rolesAllCities") : t("account.roles")}</H2>
          <div className="mt-4 flex flex-wrap gap-3" data-testid="account-roles">
            {roleList.map((rp) => (
              <div
                key={rp.id}
                className="flex flex-wrap items-center gap-3 rounded-full border border-border px-4 py-2 text-sm"
                data-testid={`account-role-${rp.role_type}-${rp.tenant_slug || tenant?.slug || ""}`}
              >
                <span>{roleLabels[lang][rp.role_type]}</span>
                {rp.tenant_name && (
                  <span className="text-xs text-muted-foreground">
                    {t("account.roleCityLabel", { city: cityLabel(rp) })}
                  </span>
                )}
                {rp.role_type === "worker" ? (
                  // Workers are app-only (D1): no web worker console to switch into.
                  <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="account-worker-note">
                    {t("account.workerAppNote")}
                    <a href={WORKER_APP_URL} className="font-medium text-[hsl(var(--primary))] underline" data-testid="account-worker-app-link">
                      {t("account.workerAppLink")}
                    </a>
                  </span>
                ) : rp.id === auth.active_role_profile_id ? (
                  <>
                    <Badge className="bg-[hsl(var(--primary))]">{t("account.usingNow")}</Badge>
                    {rp.role_type === "business_owner" && (
                      <Button size="sm" variant="outline" className="rounded-full" data-testid="account-open-owner" onClick={() => navigate("/owner")}>
                        {t("account.openOwnerConsole")}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={
                      rp.tenant_slug
                        ? `account-role-switch-${rp.role_type}-${rp.tenant_slug}`
                        : `account-role-switch-${rp.role_type}`
                    }
                    onClick={() => requestSwitch(rp)}
                  >
                    {rp.role_type === "citizen" ? t("account.switchToCitizen") : t("account.switchRole")}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 7. Sign-in & security */}
      <Card data-testid="account-signin-section">
        <H2>{t("account.signInSecurity")}</H2>
        {isStaffOrOwner && !hasEmail && (
          <p className="mt-3 rounded-xl bg-[hsl(var(--accent))]/10 px-4 py-3 text-sm" data-testid="account-email-prompt">
            {t("account.emailPrompt")}
          </p>
        )}
        <p className="mt-4 text-xs text-muted-foreground">{t("account.email")}</p>
        {hasEmail ? (
          <p data-testid="account-email">{identity.email}</p>
        ) : (
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Input
              type="email"
              className="flex-1 rounded-full"
              placeholder={t("account.emailPlaceholder")}
              data-testid="account-email-input"
              value={emailForm}
              onChange={(e) => setEmailForm(e.target.value)}
            />
            <Button className="rounded-full" disabled={emailBusy} data-testid="account-email-save" onClick={saveEmail}>
              {emailBusy ? t("common.saving") : t("account.emailAdd")}
            </Button>
          </div>
        )}
        <p className="mt-5 text-xs text-muted-foreground">{t("account.googleSignInLabel")}</p>
        {hasGoogle ? (
          // A status, not a control: there is nothing to press once Google is connected.
          <p className="text-sm" data-testid="account-google-connected">
            {t("account.googleConnectedStatus")} <span aria-hidden="true" className="text-[hsl(var(--primary))]">✓</span>
          </p>
        ) : (
          <div className="mt-2">
            <p className="mb-2 text-sm text-muted-foreground">{t("account.connectGoogleHelp")}</p>
            <GoogleLinkButton />
          </div>
        )}
        <p className="mt-5 text-xs text-muted-foreground">{t("account.changeMobile")}</p>
        <p className="text-sm text-muted-foreground" data-testid="account-change-mobile">
          {t("account.changeMobileHelp")}
        </p>
      </Card>

      {/* 8. Help & legal */}
      <Card data-testid="account-help-section">
        <H2>{t("account.helpLegal")}</H2>
        <ul className="mt-3 space-y-2 text-sm">
          {deskWa && (
            <li>
              <a className="text-[hsl(var(--primary))] underline" href={deskWa} target="_blank" rel="noreferrer" data-testid="account-desk-whatsapp">
                {t("account.cityDeskWhatsApp")}
              </a>
            </li>
          )}
          <li data-testid="account-grievance">
            {t("account.grievanceLine").replace(LEGAL_CONTACT.grievanceEmail, "")}
            <a className="text-[hsl(var(--primary))] underline" href={`mailto:${LEGAL_CONTACT.grievanceEmail}`}>
              {LEGAL_CONTACT.grievanceEmail}
            </a>
          </li>
          <li>
            <Link className="text-[hsl(var(--primary))] underline" to="/terms">
              {t("account.terms")}
            </Link>
          </li>
          <li>
            <Link className="text-[hsl(var(--primary))] underline" to="/privacy">
              {t("account.privacy")}
            </Link>
          </li>
        </ul>
      </Card>

      {/* 9. Account & privacy (DPDP): data copy, deletion, sign out */}
      <Card data-testid="account-privacy-section">
        <H2>{t("account.privacyTitle")}</H2>
        <div className="mt-4 space-y-4">
          <div>
            <Button variant="outline" className="rounded-full" data-testid="account-download-data" onClick={downloadData}>
              {t("account.downloadData")}
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">{t("account.downloadDataHint")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-full" data-testid="account-sign-out" onClick={() => setSignOutOpen(true)}>
              {t("common.signOut")}
            </Button>
            <Button variant="destructive" className="rounded-full" data-testid="account-delete" onClick={() => setDeleteOpen(true)}>
              {t("account.deleteAccount")}
            </Button>
          </div>
        </div>
      </Card>

      <ConfirmDialog
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title={t("account.signOutTitle")}
        body={t("account.signOutBody")}
        confirmLabel={t("common.signOut")}
        onConfirm={() => {
          setSignOutOpen(false);
          signOut();
          navigate("/");
        }}
        testIdPrefix="account-signout"
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("account.deleteConfirmTitle")}
        body={t("account.deleteConfirmBody")}
        confirmLabel={t("account.deleteConfirmYes")}
        destructive
        busy={deleting}
        onConfirm={async () => {
          if (await deleteAccount()) setDeleteOpen(false);
        }}
        testIdPrefix="account-delete-confirm"
      >
        <Link to="/delete-account" className="text-sm text-[hsl(var(--primary))] underline">
          {t("account.deleteConfirmMore")}
        </Link>
      </ConfirmDialog>
    </div>
  );
}
